import React, { useCallback, useEffect, useState } from "react";
import SectionPrompt from "domains/inference/components/InferenceGenerator/Sidebar/SectionPrompt";
import { getIsModelFlux } from "domains/models/utils";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import { useHandleApiError } from "infra/api/error";
import {
  GetModelsByModelIdApiResponse,
  usePutModelsByModelIdMutation,
} from "infra/api/generated/api";

import { Flex } from "@chakra-ui/react";
export interface PanelPromptEmbeddingProps {
  model: GetModelsByModelIdApiResponse["model"];
}

export default function PanelPromptEmbedding({
  model,
}: PanelPromptEmbeddingProps) {
  const handleApiError = useHandleApiError();
  const { selectedProject } = useTeamContext();

  const [prompt, setPrompt] = useState<string>(model.promptEmbedding ?? "");
  const [negativePrompt, setNegativePrompt] = useState<string>(
    model.negativePromptEmbedding ?? ""
  );

  const [updateModelTrigger] = usePutModelsByModelIdMutation();

  const updateModel = useCallback(async () => {
    try {
      await updateModelTrigger({
        projectId: selectedProject.id,
        modelId: model.id,
        body: {
          promptEmbedding: prompt,
          negativePromptEmbedding: negativePrompt,
        },
      }).unwrap();
    } catch (error) {
      handleApiError(error, "There was an error updating the model");
    }
  }, [
    handleApiError,
    model.id,
    negativePrompt,
    prompt,
    selectedProject.id,
    updateModelTrigger,
  ]);

  useEffect(() => {
    let timeout: NodeJS.Timeout | undefined;
    if (
      model.promptEmbedding !== prompt ||
      model.negativePromptEmbedding !== negativePrompt
    ) {
      timeout = setTimeout(() => {
        void updateModel();
      }, 1_000);
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [
    model.negativePromptEmbedding,
    model.promptEmbedding,
    negativePrompt,
    prompt,
    updateModel,
  ]);

  return (
    <Flex direction={"column"} gap={6}>
      <SectionPrompt
        title="Prompt"
        id="promptEmbedding"
        placeholder="Add tokens to include in your prompts"
        prompt={prompt}
        setPrompt={setPrompt}
        withRandom={false}
        withMagic={false}
        withAssetUpload={false}
        isPromptEmbedding
        withDescribeStyle={!!model.trainingImages?.length}
        getModel={async () => model}
      />

      {!getIsModelFlux(model) && (
        <SectionPrompt
          title="Negative Prompt"
          id="negativePromptEmbedding"
          placeholder={"Add tokens to include in your negative prompts"}
          prompt={negativePrompt}
          setPrompt={setNegativePrompt}
          withRandom={false}
          withMagic={false}
          withAssetUpload={false}
          withShortcuts={false}
          isPromptEmbedding
        />
      )}
    </Flex>
  );
}
